import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { Package, User, ChevronRight, Box, RotateCw, ArrowLeft, Loader2, Save } from "lucide-react";
import client, { displayName } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  requested: "bg-blue-50 text-blue-700 border-blue-200",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  quoted: "bg-violet-50 text-violet-700 border-violet-200",
  processing: "bg-sky-50 text-sky-700 border-sky-200",
  shipped: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  declined: "bg-red-50 text-red-700 border-red-200",
};
export const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center text-xs font-medium border rounded px-2 py-0.5 capitalize ${STATUS_STYLE[status] || "bg-surface text-steel border-border"}`}>{status}</span>
);
const fmtMoney = (n) => (n === null || n === undefined ? "—" : `$${Number(n).toFixed(2)}`);

const OrderDetail = ({ id }) => {
  const navigate = useNavigate();
  const { addToCart } = useStore();
  const [order, setOrder] = useState(null);
  useEffect(() => { client.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(false)); }, [id]);

  if (order === null) return <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-steel-light" /></div>;
  if (order === false) return <div className="py-20 text-center text-steel">Order not found.</div>;

  const reorder = () => { order.items.forEach((it) => addToCart(it.sku, it.qty)); toast.success("Items added to cart"); navigate("/cart"); };

  return (
    <div>
      <button onClick={() => navigate("/account?tab=orders")} className="flex items-center gap-1.5 text-sm text-steel-light hover:text-graphite mb-4"><ArrowLeft className="w-4 h-4" /> Back to orders</button>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-3"><h2 className="mono text-xl font-bold text-graphite">{order.order_no}</h2><StatusBadge status={order.status} /><span className="text-xs uppercase tracking-wider text-steel-light">{order.type}</span></div>
          <p className="text-sm text-steel-light mt-1">{new Date(order.created_at).toLocaleString()}</p>
        </div>
        <Button data-testid="reorder-btn" variant="outline" onClick={reorder}><RotateCw className="w-4 h-4 mr-2" /> Reorder</Button>
      </div>

      <div className="border border-border rounded-lg overflow-hidden bg-white">
        {order.items.map((it) => (
          <div key={it.sku} className="flex items-center gap-3 p-3 border-b border-border last:border-0">
            <Link to={`/product/${encodeURIComponent(it.sku)}`} className="w-12 h-12 rounded gp-card-img grid place-items-center p-1.5 shrink-0">{it.image ? <img src={it.image} alt="" className="max-h-full object-contain mix-blend-multiply" /> : <Box className="w-5 h-5 text-steel-light" />}</Link>
            <div className="min-w-0 flex-1"><div className="mono text-xs text-orange">{it.sku}</div><div className="text-sm text-graphite line-clamp-1">{it.name}</div></div>
            <div className="mono text-sm text-steel">×{it.qty}</div>
            <div className="mono text-sm text-graphite w-20 text-right">{fmtMoney(it.line_total)}</div>
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <div className="border border-border rounded-lg p-4 text-sm">
          <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light mb-2">Delivery</div>
          <div className="text-steel">{order.address?.line1 || "—"}</div>
          <div className="text-steel">{[order.address?.postal_code, order.address?.city, order.address?.country].filter(Boolean).join(", ")}</div>
          {order.notes && <div className="mt-2 text-steel-light">Notes: {order.notes}</div>}
          {order.admin_notes && <div className="mt-2 text-graphite bg-surface rounded p-2">Reply: {order.admin_notes}</div>}
        </div>
        <div className="border border-border rounded-lg p-4">
          <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light mb-2">Total</div>
          <div className="text-2xl font-bold text-graphite">{order.total !== null && order.total !== undefined ? `${fmtMoney(order.total)} CAD` : (order.type === "quote" ? "Awaiting quote" : "On confirmation")}</div>
        </div>
      </div>
    </div>
  );
};

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const [f, setF] = useState({ name: "", company: "", tax_id: "", phone: "", line1: "", city: "", postal_code: "", country: "Canada" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (user) setF({ name: user.name || "", company: user.company || "", tax_id: user.tax_id || "", phone: user.phone || "", line1: user.address?.line1 || "", city: user.address?.city || "", postal_code: user.address?.postal_code || "", country: user.address?.country || "Canada" });
  }, [user]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try { await updateProfile({ name: f.name, company: f.company, tax_id: f.tax_id, phone: f.phone, address: { line1: f.line1, city: f.city, postal_code: f.postal_code, country: f.country } }); toast.success("Profile updated"); }
    catch { toast.error("Failed to update"); } finally { setSaving(false); }
  };
  return (
    <div className="border border-border rounded-lg p-6 max-w-2xl">
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Full name</Label><Input data-testid="profile-name" value={f.name} onChange={set("name")} className="mt-1.5 h-11" /></div>
          <div><Label>Company</Label><Input data-testid="profile-company" value={f.company} onChange={set("company")} className="mt-1.5 h-11" /></div>
        </div>
        <div><Label>Email</Label><Input value={user?.email || ""} disabled className="mt-1.5 h-11 bg-surface" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Tax ID / NIF</Label><Input value={f.tax_id} onChange={set("tax_id")} className="mt-1.5 h-11" /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={set("phone")} className="mt-1.5 h-11" /></div>
        </div>
        <div><Label>Address</Label><Input value={f.line1} onChange={set("line1")} className="mt-1.5 h-11" /></div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label>City</Label><Input value={f.city} onChange={set("city")} className="mt-1.5 h-11" /></div>
          <div><Label>Postal code</Label><Input value={f.postal_code} onChange={set("postal_code")} className="mt-1.5 h-11" /></div>
          <div><Label>Country</Label><Input value={f.country} onChange={set("country")} className="mt-1.5 h-11" /></div>
        </div>
        <Button data-testid="save-profile" onClick={save} disabled={saving} className="bg-orange hover:bg-orange-hover">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}</Button>
      </div>
    </div>
  );
};

const OrdersList = () => {
  const [orders, setOrders] = useState(null);
  useEffect(() => { client.get("/orders").then((r) => setOrders(r.data.items)); }, []);
  if (orders === null) return <div className="py-12 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-steel-light" /></div>;
  if (!orders.length) return (
    <div className="border border-border rounded-lg py-16 text-center"><Package className="w-10 h-10 text-steel-light mx-auto mb-3" /><p className="text-steel font-medium">No orders yet</p><Link to="/catalogue" className="text-orange text-sm">Browse catalogue</Link></div>
  );
  return (
    <div className="space-y-2">
      {orders.map((o) => (
        <Link key={o.id} to={`/account/orders/${o.id}`} data-testid={`order-${o.order_no}`} className="flex items-center justify-between gap-4 p-4 bg-white border border-border rounded-lg hover:border-graphite/40 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-surface grid place-items-center"><Package className="w-5 h-5 text-steel-light" /></div>
            <div><div className="mono text-sm font-semibold text-graphite">{o.order_no}</div><div className="text-xs text-steel-light">{o.items.length} items · {new Date(o.created_at).toLocaleDateString()} · {o.type}</div></div>
          </div>
          <div className="flex items-center gap-3"><StatusBadge status={o.status} /><span className="mono text-sm text-graphite hidden sm:block">{o.total !== null && o.total !== undefined ? `$${Number(o.total).toFixed(2)}` : "—"}</span><ChevronRight className="w-4 h-4 text-steel-light" /></div>
        </Link>
      ))}
    </div>
  );
};

const Account = () => {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const tab = sp.get("tab") === "orders" ? "orders" : "profile";

  return (
    <div className="min-h-screen flex flex-col bg-white"><Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 w-full py-8 flex-1">
        <h1 className="text-2xl font-bold tracking-tight text-graphite">My Account</h1>
        <p className="text-sm text-steel-light mt-1 mb-6">{user?.company ? `${user.company} · ` : ""}{user?.email}</p>

        {id ? <OrderDetail id={id} /> : (
          <Tabs defaultValue={tab}>
            <TabsList>
              <TabsTrigger value="profile" data-testid="tab-profile"><User className="w-4 h-4 mr-1.5" /> Profile</TabsTrigger>
              <TabsTrigger value="orders" data-testid="tab-orders"><Package className="w-4 h-4 mr-1.5" /> My Orders</TabsTrigger>
            </TabsList>
            <TabsContent value="profile" className="mt-5"><Profile /></TabsContent>
            <TabsContent value="orders" className="mt-5"><OrdersList /></TabsContent>
          </Tabs>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Account;
